import { Slot, SlotFillProvider, withFilters }                         from '@wordpress/components';
import { __ }                                                          from '@wordpress/i18n';
import { Formik, Form, Field }                                         from 'formik';
import React                                                           from 'react';
import SettingsFormContainerPropsInterface                             from '../../../interfaces/SettingsFormContainerPropsInterface';
import SettingsFormPropsPlanInterface                                  from '../../../interfaces/SettingsFormPropsPlanInterface';
import AdminPageSection                                                from '../AdminPageSection';
import CodeEditorField                                                 from '../Fields/CodeEditorField';
import MediumAlert                                                     from '../MediumAlert';
import SevereAlert                                                     from '../SevereAlert';

interface AdvancedScriptsSettingsInterface {
    header_scripts: string;
    footer_scripts: string;
    php_snippets: string;
    header_scripts_checkout: string;
    footer_scripts_checkout: string;
    header_scripts_thank_you: string;
    footer_scripts_thank_you: string;
    header_scripts_order_pay: string;
    footer_scripts_order_pay: string;
}

interface AdvancedScriptsSettingsFormPropsInterface extends SettingsFormContainerPropsInterface {
    settings: AdvancedScriptsSettingsInterface;
    plan: SettingsFormPropsPlanInterface;

}

const AdvancedScriptsSettingsForm: React.FC<AdvancedScriptsSettingsFormPropsInterface> = ( props ) => {
    const { saveSettings, isLoading, searchTerm } = props;

    const AdditionalSettings = withFilters(
        'CheckoutWC.Admin.Settings.Pages.AdvancedScripts',
    )( () => <></> );

    return (
        <SlotFillProvider>
            <AdditionalSettings />
            <Formik
                initialValues={{
                    ...props.settings,
                }}
                enableReinitialize={true}
                onSubmit={async ( values ) => {
                    await saveSettings( values );
                }}
            >
                {( { values } ) => (
                    <Form className={'space-y-6 transition-all'} style={{ filter: isLoading ? 'blur(2px)' : 'none' }}>
                        <AdminPageSection
                            title={__( 'Global', 'checkout-wc' )}
                            description={__( 'Add custom scripts that run on all enabled CheckoutWC templates. (Checkout, Thank You, Order Pay)', 'checkout-wc' )}
                            content={
                                <>
                                    <Field
                                        name="header_scripts"
                                        label={__( 'Header Scripts', 'checkout-wc' )}
                                        language='html'
                                        component={CodeEditorField}
                                        description={__( 'This code will output immediately before the closing <code>&lt;/head&gt;</code> tag in the document source.', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />

                                    <Field
                                        name="footer_scripts"
                                        label={__( 'Footer Scripts', 'checkout-wc' )}
                                        language='html'
                                        component={CodeEditorField}
                                        description={__( 'This code will output immediately before the closing <code>&lt;/body&gt;</code> tag in the document source.', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />

                                    { ( values.header_scripts.includes( '<?' ) || values.footer_scripts.includes( '<?' ) )
                                        && (
                                            <MediumAlert
                                                description={__( 'Header and Footer Scripts are HTML only. For PHP, use PHP Snippets option.', 'checkout-wc' )}
                                            />
                                        )
                                    }

                                    <Field
                                        name="php_snippets"
                                        label={__( 'PHP Snippets', 'checkout-wc' )}
                                        language='php'
                                        component={CodeEditorField}
                                        description={
                                            // translators: %s: URL to Code Snippets plugin
                                            __( 'Add PHP snippets to modify your checkout page here. If you have lots of snippets, you may want to consider using <a class="text-blue-600 underline" target="_blank" href="%s">Code Snippets</a>.', 'checkout-wc' )
                                        }
                                        searchTerm={searchTerm}
                                    />

                                    { values.php_snippets.startsWith( '<?' )
                                        && (
                                            <SevereAlert
                                                description={__( 'Please omit the opening PHP tag.', 'checkout-wc' )}
                                            />
                                        )
                                    }

                                    <Slot name="CheckoutWC.Admin.Pages.AdvancedScripts.Global" />
                                </>
                            }
                        />

                        <AdminPageSection
                            title={__( 'Checkout', 'checkout-wc' )}
                            description={__( 'Add custom JavaScript and PHP that runs on the checkout page.', 'checkout-wc' )}
                            content={
                                <>
                                    <Field
                                        name="header_scripts_checkout"
                                        label={__( 'Header Scripts', 'checkout-wc' )}
                                        language='html'
                                        component={CodeEditorField}
                                        description={__( 'This code will output immediately before the closing <code>&lt;/head&gt;</code> tag in the document source.', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />

                                    <Field
                                        name="footer_scripts_checkout"
                                        label={__( 'Footer Scripts', 'checkout-wc' )}
                                        language='html'
                                        component={CodeEditorField}
                                        description={__( 'This code will output immediately before the closing <code>&lt;/body&gt;</code> tag in the document source.', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />

                                    { ( values.header_scripts_checkout.includes( '<?' ) || values.footer_scripts_checkout.includes( '<?' ) )
                                        && (
                                            <MediumAlert
                                                description={__( 'Header and Footer Scripts are HTML only. For PHP, use PHP Snippets option.', 'checkout-wc' )}
                                            />
                                        )
                                    }

                                    <Slot name="CheckoutWC.Admin.Pages.AdvancedScripts.Global" />
                                </>
                            }
                        />

                        <AdminPageSection
                            title={__( 'Thank You', 'checkout-wc' )}
                            description={__( 'Add custom JavaScript and PHP that runs on the Order Received / Thank You page.', 'checkout-wc' )}
                            content={
                                <>
                                    <Field
                                        name="header_scripts_thank_you"
                                        label={__( 'Header Scripts', 'checkout-wc' )}
                                        language='html'
                                        component={CodeEditorField}
                                        description={__( 'This code will output immediately before the closing <code>&lt;/head&gt;</code> tag in the document source.', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />

                                    <Field
                                        name="footer_scripts_thank_you"
                                        label={__( 'Footer Scripts', 'checkout-wc' )}
                                        language='html'
                                        component={CodeEditorField}
                                        description={__( 'This code will output immediately before the closing <code>&lt;/body&gt;</code> tag in the document source.', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />

                                    { ( values.header_scripts_thank_you.includes( '<?' ) || values.footer_scripts_thank_you.includes( '<?' ) )
                                        && (
                                            <MediumAlert
                                                description={__( 'Header and Footer Scripts are HTML only. For PHP, use PHP Snippets option.', 'checkout-wc' )}
                                            />
                                        )
                                    }

                                    <Slot name="CheckoutWC.Admin.Pages.AdvancedScripts.Global" />
                                </>
                            }
                        />

                        <AdminPageSection
                            title={__( 'Order Pay', 'checkout-wc' )}
                            description={__( 'Add custom JavaScript and PHP that runs on the Order Pay page.', 'checkout-wc' )}
                            content={
                                <>
                                    <Field
                                        name="header_scripts_order_pay"
                                        label={__( 'Header Scripts', 'checkout-wc' )}
                                        language='html'
                                        component={CodeEditorField}
                                        description={__( 'This code will output immediately before the closing <code>&lt;/head&gt;</code> tag in the document source.', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />

                                    <Field
                                        name="footer_scripts_order_pay"
                                        label={__( 'Footer Scripts', 'checkout-wc' )}
                                        language='html'
                                        component={CodeEditorField}
                                        description={__( 'This code will output immediately before the closing <code>&lt;/body&gt;</code> tag in the document source.', 'checkout-wc' )}
                                        searchTerm={searchTerm}
                                    />

                                    { ( values.header_scripts_order_pay.includes( '<?' ) || values.footer_scripts_order_pay.includes( '<?' ) )
                                        && (
                                            <MediumAlert
                                                description={__( 'Header and Footer Scripts are HTML only. For PHP, use PHP Snippets option.', 'checkout-wc' )}
                                            />
                                        )
                                    }

                                    <Slot name="CheckoutWC.Admin.Pages.AdvancedScripts.Global" />
                                </>
                            }
                        />

                        <button className={'cfw_admin_page_submit hidden'} type="submit">Submit</button>
                    </Form>
                )}
            </Formik>
        </SlotFillProvider>
    );
};

export default AdvancedScriptsSettingsForm;
